import { z } from 'zod';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { toUserId } from '@/shared/types/common.types';
import { DENSITIES, THEMES } from '@/shared/types/preferences.types';
import { identityApiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import type {
  ProfileFormValues,
  SettingsFormValues,
  UserProfile,
  UserSettings,
} from '../types/profile.types';

const profileSchema = z.object({
  id: z.string().min(1),
  username: z.string(),
  email: z.string(),
  givenName: z.string(),
  familyName: z.string(),
  phoneNumber: z.string(),
  jobTitle: z.string(),
  department: z.string(),
  organization: z.string(),
  employeeId: z.string(),
  avatarUrl: z.string().nullable(),
  roles: z.array(z.string()),
  lastSignInAt: z.string(),
});

const settingsSchema = z.object({
  theme: z.enum(THEMES),
  density: z.enum(DENSITIES),
  locale: z.string(),
  timeZone: z.string(),
  emailOnAuditFailure: z.boolean(),
  emailOnModelChange: z.boolean(),
  defaultPageSize: z.number().int(),
});

function toProfile(raw: z.infer<typeof profileSchema>): UserProfile {
  return { ...raw, id: toUserId(raw.id) };
}

class ProfileService {
  async getProfile(): Promise<UserProfile> {
    const payload = await identityApiClient.get<unknown>(API_ENDPOINTS.profile.me);
    return toProfile(parseOrThrow(profileSchema, payload, 'user profile'));
  }

  async updateProfile(values: ProfileFormValues): Promise<UserProfile> {
    const payload = await identityApiClient.put<unknown>(API_ENDPOINTS.profile.update, values);
    return toProfile(parseOrThrow(profileSchema, payload, 'user profile'));
  }

  async getSettings(): Promise<UserSettings> {
    const payload = await identityApiClient.get<unknown>(API_ENDPOINTS.profile.settings);
    return parseOrThrow(settingsSchema, payload, 'user preferences');
  }

  async updateSettings(values: SettingsFormValues): Promise<UserSettings> {
    const payload = await identityApiClient.put<unknown>(API_ENDPOINTS.profile.settings, values);
    return parseOrThrow(settingsSchema, payload, 'user preferences');
  }
}

export const profileService = new ProfileService();
